import moment from 'moment';
import Quill, { Delta } from 'quill/core';
import type { Range } from 'quill/core/selection';
import React, { useEffect, useMemo, useState } from 'react';
import type { ChevronProps, DayButtonProps } from 'react-day-picker';
import BorderedIcon from '../components/BorderedIcon';
import Caret from '../components/Caret';
import DayButton from '../components/DayButton';
import DayPicker from '../components/DayPicker';
import DropdownMenu from '../components/DropdownMenu';
import FloatingArrow from '../components/FloatingArrow';
import SelectEntityOption from '../components/SelectEntityOption';
import type QuillEditor from '../editor/QuillEditor';
import { getRangeSaver } from '../editor/RangeSaver';
import { formatSearchDate } from '../editor/formats/SearchFacet';
import filterIcon from '../icons/non-prefilled/16x16/filter.svg';
import { useFacetEdit } from './FacetEditContext';
import * as styles from './SearchDatePicker.module.scss';
import type { DateFacet } from './quillFacets';
import { createSearchFacetOp, getSeparator } from './quillFacets';

interface SearchDatePickerProps {
  onClose: () => void;
  searchFormRef: React.RefObject<HTMLFormElement | null>;
  facet: DateFacet;
  text: string;
  quill: QuillEditor;
  range: Range;
}

const Chevron = (props: ChevronProps) => (
  <Caret direction={props.orientation} block size={10} />
);

const SearchDateDayButton = (props: DayButtonProps) => {
  if (props.modifiers.hidden) {
    // react-day-picker requires an empty fragment
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return <DayButton {...props} />;
};

const SearchDatePicker = ({
  onClose,
  searchFormRef,
  facet,
  text,
  quill,
  range,
}: SearchDatePickerProps) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [focusedIndex, setFocusedIndex] = useState(0);
  const { isEditingFacet } = useFacetEdit();

  const currentDate = useMemo(() => {
    const withYear = moment(text, 'MMM DD, YYYY');
    const withoutYear = moment(text, 'MMM DD');

    if (withYear.isValid()) return withYear.toDate();
    if (withoutYear.isValid()) return withoutYear.toDate();
    return new Date();
  }, [text]);

  useEffect(() => {
    setCurrentMonth(currentDate);
  }, [currentDate]);

  const onSelectDay = (date: Date) => {
    const selection = getRangeSaver(quill).getLast();

    const delta = new Delta()
      .retain(range.index)
      .delete(selection.index - range.index)
      .insert(createSearchFacetOp(facet, date.toISOString()).insert);

    quill.updateContents(delta, Quill.sources.USER);
  };

  return (
    <div className={styles.container}>
      {!isEditingFacet && (
        <DropdownMenu.Root
          hotkeysEnabled
          index={focusedIndex}
          onIndexChange={setFocusedIndex}
          sections={[
            {
              items: [
                {
                  id: 'suggested',
                  data: {
                    type: 'searchOption',
                    icon: <BorderedIcon>{filterIcon}</BorderedIcon>,
                    label: (
                      <div>
                        <div className={styles.label}>
                          {facet.comparator === 'after'
                            ? 'Later than'
                            : 'Earlier than'}
                        </div>
                        <div className={styles.content}>
                          {facet.prefix}
                          {getSeparator(facet)}
                          {formatSearchDate(moment(currentDate))}
                        </div>
                      </div>
                    ),
                    query: '',
                  },
                },
              ],
            },
          ]}
          onSelect={() => onSelectDay(currentDate)}
          onClose={onClose}
          inputContainer={searchFormRef.current}
          renderItem={(data) => <SelectEntityOption {...data} />}
        />
      )}

      <div className={styles.dayPickerContainer}>
        <DayPicker
          className={styles.dayPicker}
          mode="single"
          numberOfMonths={1}
          selected={currentDate}
          onMonthChange={setCurrentMonth}
          onDayClick={onSelectDay}
          onSelect={(date) => {
            if (!date) return;
            onSelectDay(date);
          }}
          month={currentMonth}
          components={{ DayButton: SearchDateDayButton, Chevron }}
        />
        <FloatingArrow placement="bottom" arrowData={{ x: 48 }} />
      </div>
    </div>
  );
};

export default SearchDatePicker;
